import { useState, useMemo, useCallback } from 'react';
import { classifyCoverage } from '@leopart/calc';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Search } from 'lucide-react';
import { ITeam } from '../../models/Team';
import { useEndpoints } from '../../hooks/useEndpoints';
import { useDetailParam } from '../../hooks/useDetailParam';
import { formatFullName } from '../../utils/functions';
import DateNavigator from './DateNavigator';
import styles from './ReadinessView.module.scss';
import { TeamReadinessResponse, TeamReadinessAthlete, InjuredAthleteSummary } from '../types';
import { ComplaintDetail } from '../../shared/ComplaintBadge/ComplaintBadge';
import HooperHistoryModal, { HooperMetric } from '../../components/DetailTeamPage/SubjectReadinessTab/HooperHistoryModal';
import MetricHistoryModal from './MetricHistoryModal/MetricHistoryModal';
import { MetricKey } from './MetricHistoryModal/metricHistory.constants';
import PageHelpButton from '../../shared/PageHelpButton/PageHelpButton';
import ReadinessMatrixRow from './ReadinessMatrixRow';
import ReadinessCard from './ReadinessCard';
import InjuredAthletesSection from './InjuredAthletesSection';
import { getDomainColor } from './readinessColors';

// ─── Props ────────────────────────────────────────────────────────────────────

interface TeamReadinessTabProps {
  team: ITeam<any>;
  selectedDate: string;
  onDateChange: (date: string) => void;
  viewMode: 'matrix' | 'card';
  /** Click sulla colonna sRPE → naviga alla tab Sforzo */
  onGoToEffort?: () => void;
}

// ─── Skeleton rows/cards ──────────────────────────────────────────────────────

function SkeletonCell() {
  return (
    <td className={styles.tdClickable}>
      <div style={{ width: 32, height: 12, background: 'var(--c-card-border)', borderRadius: 4, margin: '0 auto' }} />
    </td>
  );
}

function MatrixSkeletonRow({ index }: { index: number }) {
  return (
    <tr key={index} className={styles.tableRow}>
      <td className={styles.tdNameSticky}>
        <div style={{ width: 120, height: 14, background: 'var(--c-card-border)', borderRadius: 4 }} />
      </td>
      <td className={styles.tdRiCenter}>
        <div style={{ width: 32, height: 20, background: 'var(--c-card-border)', borderRadius: 4, margin: '0 auto' }} />
      </td>
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
      <SkeletonCell />
    </tr>
  );
}

function CardSkeletonItem({ index }: { index: number }) {
  return (
    <div key={index} className={styles.athleteCard} style={{ pointerEvents: 'none' }}>
      <div className={styles.cardHeader}>
        <div style={{ width: 100, height: 14, background: 'var(--c-card-border)', borderRadius: 4 }} />
        <div style={{ width: 32, height: 20, background: 'var(--c-card-border)', borderRadius: 4 }} />
      </div>
      <div className={styles.cardDomainsGrid}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.cardDomainCell}>
            <div style={{ width: 12, height: 12, background: 'var(--c-card-border)', borderRadius: '50%', margin: '0 auto 4px' }} />
            <div style={{ width: 24, height: 16, background: 'var(--c-card-border)', borderRadius: 4, margin: '0 auto' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SelectedCell ─────────────────────────────────────────────────────────────

interface SelectedCell {
  athleteId: string;
  athleteName: string;
  metric: HooperMetric;
}

interface SelectedMetricCell {
  athleteId: string;
  athleteName: string;
  metric: MetricKey;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TeamReadinessTab({ team, selectedDate, onDateChange, viewMode, onGoToEffort }: TeamReadinessTabProps) {
  const endpoints = useEndpoints();
  const [domainFilter, setDomainFilter] = useState<'all' | 'mental' | 'muscular' | 'cardio' | 'strain'>('all');
  const [athleteSearch, setAthleteSearch] = useState('');
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [selectedMetricCell, setSelectedMetricCell] = useState<SelectedMetricCell | null>(null);
  const { detailKind, isDetailOpen, openDetail, closeDetail } = useDetailParam();

  const handleCellClick = useCallback((metric: HooperMetric, item: TeamReadinessAthlete) => {
    setSelectedCell({
      athleteId: item.athleteId,
      athleteName: formatFullName(item.firstName, item.lastName),
      metric,
    });
    openDetail('hooper');
  }, [openDetail]);

  const handleMetricCellClick = useCallback((metric: MetricKey, item: TeamReadinessAthlete) => {
    setSelectedMetricCell({
      athleteId: item.athleteId,
      athleteName: formatFullName(item.firstName, item.lastName),
      metric,
    });
    openDetail('metric');
  }, [openDetail]);

  // Single API call for the whole team
  const { data, isLoading } = useQuery<TeamReadinessResponse>({
    queryKey: ['team-readiness', team._id, selectedDate],
    queryFn: async () => {
      const { data } = await endpoints.getTeamReadiness(team._id, { date: selectedDate });
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Body map complaints for the team on the selected date
  const { data: bodyMapData } = useQuery<any[]>({
    queryKey: ['bodymap-team-day', team._id, selectedDate],
    queryFn: async () => {
      const { data: bm } = await endpoints.getBodyMapTeam(team._id, { date: selectedDate });
      return Array.isArray(bm) ? bm : [];
    },
    enabled: !!(data?.athletes?.some((a) => a.hasComplaints)),
    staleTime: 1000 * 60 * 5,
  });

  // Map: athleteId → complaint details
  const complaintsByAthlete = useMemo(() => {
    const map: Record<string, ComplaintDetail[]> = {};
    if (!bodyMapData) return map;
    for (const entry of bodyMapData) {
      const athleteId = entry.userId ?? entry.athleteId;
      if (athleteId && Array.isArray(entry.complaints)) {
        map[athleteId] = entry.complaints as ComplaintDetail[];
      }
    }
    return map;
  }, [bodyMapData]);

  const athletesList = useMemo(() => {
    const withData = data?.athletes ?? [];
    const withoutData: TeamReadinessAthlete[] = (data?.athletesWithoutData ?? []).map((a) => ({
      athleteId: a.athleteId,
      firstName: a.firstName,
      lastName: a.lastName,
      ri: null,
      metrics: { hooper: null, cmj: null, fcr: null, hrv: null, sRPE: null, acwr: null },
      factorsPresent: [],
      strain: null,
      sparkline: [],
      hasComplaints: false,
    }));
    return [...withData, ...withoutData];
  }, [data]);

  // Check if an athlete has data for a given domain
  const hasDomainData = (item: TeamReadinessAthlete, domain: 'mental' | 'muscular' | 'cardio' | 'strain'): boolean => {
    if (!item.ri) return false;
    switch (domain) {
      case 'mental':   return item.metrics?.hooper != null;
      case 'muscular': return item.metrics?.cmj != null;
      case 'cardio':   return item.metrics?.hrv != null || item.metrics?.fcr != null;
      case 'strain':   return false; // strain data not present in v2 response
      default:         return false;
    }
  };

  // Count athletes with data per domain (for pill badges)
  const domainCounts = useMemo(() => {
    const total = athletesList.length;
    const completed = athletesList.filter((a) => a.ri != null).length;
    const mental = athletesList.filter((a) => hasDomainData(a, 'mental')).length;
    const muscular = athletesList.filter((a) => hasDomainData(a, 'muscular')).length;
    const cardio = athletesList.filter((a) => hasDomainData(a, 'cardio')).length;
    const strain = athletesList.filter((a) => hasDomainData(a, 'strain')).length;
    return { all: `${completed}/${total}`, mental, muscular, cardio, strain };
  }, [athletesList, data]);

  // Mappa infortuni attivi per athleteId
  const injuredMap = useMemo(() => {
    const map: Record<string, InjuredAthleteSummary> = {};
    (data?.injuredAthletes ?? []).forEach((inj) => {
      map[inj.athleteId] = inj;
    });
    return map;
  }, [data?.injuredAthletes]);

  // Filter by search + domain
  const filteredList = useMemo(() => {
    let list = athletesList;

    // Domain filter: show only athletes that have data for selected domain
    if (domainFilter !== 'all') {
      list = list.filter((a) => hasDomainData(a, domainFilter));
    }

    // Search
    if (athleteSearch) {
      const q = athleteSearch.toLowerCase();
      list = list.filter((a) =>
        `${a.firstName} ${a.lastName}`.toLowerCase().includes(q)
      );
    }

    return list;
  }, [athletesList, athleteSearch, domainFilter]);

  // Sort for matrix (worst first — low scores on top so coach sees problems first)
  const sortedListMatrix = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const riA = a.ri;
      const riB = b.ri;

      // Athletes without data go to bottom
      if (!riA && riB) return 1;
      if (riA && !riB) return -1;

      // sotto_stress status first
      if (riA?.status === 'sotto_stress' && riB?.status !== 'sotto_stress') return -1;
      if (riB?.status === 'sotto_stress' && riA?.status !== 'sotto_stress') return 1;

      // Sort by ri value ascending (worst first)
      if (riA && riB) {
        if (riA.value !== riB.value) return riA.value - riB.value;
      }

      return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    });
  }, [filteredList, domainFilter]);

  // Sort for cards (best first)
  const sortedListCards = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const riA = a.ri;
      const riB = b.ri;

      if (!riA && riB) return 1;
      if (riA && !riB) return -1;

      if (riA && riB) {
        if (riA.value !== riB.value) return riB.value - riA.value;
      }

      return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    });
  }, [filteredList, domainFilter]);

  const skeletonCount = team.users?.length || 6;

  const domainFilterLabels: Record<string, string> = {
    all: 'Tutti',
    mental: 'Mentale',
    muscular: 'Muscolare',
    cardio: 'Cardio',
    strain: 'Strain',
  };

  const filterBar = (
    <div className={styles.filterBar}>
      <div className={styles.filterPills}>
        {(['all', 'mental', 'muscular', 'cardio', 'strain'] as const).map((f) => {
          const count = f === 'all' ? domainCounts.all : domainCounts[f];
          return (
            <button
              key={f}
              onClick={() => setDomainFilter(f)}
              className={[styles.filterPill, domainFilter === f ? styles.filterPillActive : ''].filter(Boolean).join(' ')}
            >
              {domainFilterLabels[f]}
              {!isLoading && (
                <span style={{ marginLeft: 4, opacity: 0.7, fontSize: 10 }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <DateNavigator selectedDate={selectedDate} onDateChange={onDateChange} />
      <PageHelpButton pageKey="page-readiness" />
    </div>
  );

  const summary = data?.summary;
  const compilationPct = summary ? Math.round((summary.athletesWithData / Math.max(summary.totalAthletes, 1)) * 100) : 0;

  const summaryBar = summary && !isLoading ? (
    <div className={styles.summaryBar}>
      {/* RI Medio */}
      <div className={styles.summaryBlock}>
        <span className={styles.summaryLabelRow}>
          <span className={styles.summaryLabel}>RI Medio</span>
        </span>
        <span className={styles.summaryValue} style={{ color: summary.riMean != null ? getDomainColor(summary.riMean) : '#b5b5b5' }}>
          {summary.riMean != null ? summary.riMean.toFixed(1) : '—'}
        </span>
      </div>

      <div className={styles.summaryDivider} />

      {/* Compilazione */}
      <div className={styles.summaryBlock}>
        <span className={styles.summaryLabelRow}>
          <span className={styles.summaryLabel}>Compilazione</span>
        </span>
        <div className={styles.summaryCompilation}>
          <span className={styles.summaryValueLg}>{summary.athletesWithData}</span>
          <span className={styles.summaryValueSub}>/{summary.totalAthletes}</span>
          <div className={styles.summaryProgressTrack}>
            <div
              className={styles.summaryProgressFill}
              style={{
                width: `${compilationPct}%`,
                background:
                  classifyCoverage(compilationPct) === 'good'
                    ? '#27ae60'
                    : classifyCoverage(compilationPct) === 'warn'
                      ? '#FFD15A'
                      : '#f57c73',
              }}
            />
          </div>
        </div>
      </div>

      {/* Badge warnings */}
      {(summary.lowConfidenceCount > 0 || summary.calibrationCount > 0) && (
        <>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryBadges}>
            {summary.lowConfidenceCount > 0 && (
              <span className={styles.summaryBadgeWarn}>
                {summary.lowConfidenceCount} bassa conf.
              </span>
            )}
            {summary.calibrationCount > 0 && (
              <span className={styles.summaryBadgeCal}>
                {summary.calibrationCount} in calibrazione
              </span>
            )}
          </div>
        </>
      )}

      {/* Representative warning */}
      {summary.representativeWarning && (
        <div className={styles.summaryWarning}>
          <AlertCircle size={13} />
          <span>{summary.representativeWarning}</span>
        </div>
      )}
    </div>
  ) : null;

  // ── Matrix view ─────────────────────────────────────────────────────────────
  if (viewMode === 'matrix') {
    return (
      <>
        {filterBar}
        {summaryBar}
        <div className={styles.tableCard}>
          <div className={styles.tableScrollWrapper}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.thSearchSticky}>
                    <div className={styles.searchWrapper}>
                      <Search className={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder="Cerca atleta..."
                        value={athleteSearch}
                        onChange={(e) => setAthleteSearch(e.target.value)}
                        className={styles.searchInput}
                      />
                    </div>
                  </th>
                  <th className={styles.thRiCenter}>
                    <p className={styles.thGroupLabel}>RI</p>
                  </th>
                  <th colSpan={3} className={styles.thGroupMental}>
                    <p className={styles.thGroupLabel}>MENTALE (PRE)</p>
                  </th>
                  <th colSpan={2} className={styles.thGroupMuscular}>
                    <p className={styles.thGroupLabel}>MUSCOLARE (PRE)</p>
                  </th>
                  <th colSpan={2} className={styles.thGroupCardio}>
                    <p className={styles.thGroupLabel}>CARD. (PRE)</p>
                  </th>
                  <th colSpan={2} className={styles.thGroupStrain}>
                    <p className={styles.thGroupLabel}>STRAIN (POST)</p>
                  </th>
                  <th colSpan={1} className={styles.thGroupStrain}>
                    <p className={styles.thGroupLabel}>TREND</p>
                  </th>
                </tr>
                <tr className={styles.thSubRow}>
                  <th className={styles.thSubEmpty}></th>
                  <th className={styles.thSubRi}></th>
                  <th className={styles.thSubCell}><p className={styles.thSubLabel}>Sonno</p></th>
                  <th className={styles.thSubCell}><p className={styles.thSubLabel}>Stress</p></th>
                  <th className={styles.thSubCellBorderR}><p className={styles.thSubLabel}>Umore</p></th>
                  <th className={styles.thSubCell}><p className={styles.thSubLabel}>CMJ</p></th>
                  <th className={styles.thSubCellBorderR}><p className={styles.thSubLabel}>DOMS</p></th>
                  <th className={styles.thSubCell}><p className={styles.thSubLabel}>HRrest</p></th>
                  <th className={styles.thSubCellBorderR}><p className={styles.thSubLabel}>HRV</p></th>
                  <th className={onGoToEffort ? styles.thSubCellLink : styles.thSubCell} onClick={onGoToEffort} title="Vai alla tab Sforzo"><p className={styles.thSubLabel}>sRPE</p></th>
                  <th className={styles.thSubCell}><p className={styles.thSubLabel}>ACWR</p></th>
                  <th className={styles.thSubCell}><p className={styles.thSubLabel}>Trend</p></th>
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: skeletonCount }).map((_, i) => <MatrixSkeletonRow key={i} index={i} />)
                  : sortedListMatrix.map((item) => (
                      <ReadinessMatrixRow
                        key={item.athleteId}
                        item={item}
                        complaintsByAthlete={complaintsByAthlete}
                        injuredMap={injuredMap}
                        onHooperCellClick={handleCellClick}
                        onMetricCellClick={handleMetricCellClick}
                        onGoToEffort={onGoToEffort}
                      />
                    ))}
              </tbody>
            </table>
          </div>
        </div>

        <InjuredAthletesSection injured={data?.injuredAthletes ?? []} />

        <HooperHistoryModal
          isOpen={isDetailOpen && detailKind === 'hooper'}
          onClose={closeDetail}
          athleteId={selectedCell?.athleteId ?? null}
          athleteName={selectedCell?.athleteName ?? null}
          metric={selectedCell?.metric ?? null}
        />
        <MetricHistoryModal
          isOpen={isDetailOpen && detailKind === 'metric'}
          onClose={closeDetail}
          athleteId={selectedMetricCell?.athleteId ?? null}
          athleteName={selectedMetricCell?.athleteName ?? null}
          metric={selectedMetricCell?.metric ?? null}
          highlightDate={selectedDate}
        />
      </>
    );
  }

  // ── Card view ────────────────────────────────────────────────────────────────
  return (
    <>
      {filterBar}
      {summaryBar}
      <div className={styles.cardGrid}>
        {isLoading
          ? Array.from({ length: skeletonCount }).map((_, i) => <CardSkeletonItem key={i} index={i} />)
          : sortedListCards.map((item) => (
              <ReadinessCard
                key={item.athleteId}
                item={item}
                complaintsByAthlete={complaintsByAthlete}
                injuredMap={injuredMap}
              />
            ))}
      </div>

      <InjuredAthletesSection injured={data?.injuredAthletes ?? []} />

      <HooperHistoryModal
        isOpen={isDetailOpen && detailKind === 'hooper'}
        onClose={closeDetail}
        athleteId={selectedCell?.athleteId ?? null}
        athleteName={selectedCell?.athleteName ?? null}
        metric={selectedCell?.metric ?? null}
      />
    </>
  );
}
